import { lazy, Suspense, useEffect, useLayoutEffect, useState, type CSSProperties } from 'react';
import { useLocation, useParams } from 'react-router';
import type { SessionUser } from '@/features/auth/api';
import { startClockTicker, useUI, type Mode } from '@/state/store';
import { cn } from '@/lib/cn';
import { TopBar } from './TopBar';
import { useDeepLink } from './useDeepLink';
import { CommandStrip } from './CommandStrip';
import { CenterStage } from './CenterStage';
import { DetailPanel } from './DetailPanel';
import { DetailSheet } from './DetailSheet';
import { BottomPanel } from './BottomPanel';
import {
  MODE_PANEL_ID,
  detailPresentation,
  modeTabId,
  sheetHeightPx,
  stripPresentation,
  toastPlacement,
  type SheetSnap,
} from './layout';
import { useElementSize, useViewportWidth } from './useViewport';
import { Toaster } from '@/components/ui/toast';
import { LoaderRuntime } from '@/features/loader/LoaderRuntime';
import { AwarenessRuntime } from '@/features/loader/AwarenessRuntime';
import { LiveTleRuntime } from '@/features/loader/catalog/LiveTleRuntime';
import { CatalogModal } from '@/features/loader/ui/CatalogModal';
import { TwinRuntime } from '@/features/twin/TwinRuntime';
import { TwinDialogs } from '@/features/twin/ui';
const GaiaColumn = lazy(() => import('@/features/gaia/GaiaColumn').then((m) => ({ default: m.GaiaColumn })));
import { TwinLiveRuntime } from '@/features/twin/telemetry/TwinLiveRuntime';
import { PresenterOverlay, usePresenterKeys } from '@/features/presenter';
import { DiveOverlay } from '@/features/dive/DiveOverlay';
import { selectTwinCatalogue, useCatalogueView } from '@/features/catalogue/view';

/** Keyboard shortcuts stay out of the way of typing and of open menus, dialogs and listboxes. */
function shortcutBlocked(t: HTMLElement | null): boolean {
  if (!t) return false;
  if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)
    return true;
  return Boolean(t.closest('[role="menu"], [role="dialog"], [role="listbox"]'));
}

/**
 * The unified command surface: top bar, [command strip | stage | detail], bottom panel.
 * From 1100 px the detail panel is a 360 px side column; below it, a bottom sheet over
 * the stage (layout.ts). The stage column isolates its stacking context, so the strip's
 * overlay and the tablet GAIA column float above everything the engines draw.
 */
export function Shell({ mode, user }: { mode: Mode; user: SessionUser }) {
  const { assetId } = useParams();
  const { search } = useLocation();
  const rightPanelOpen = useUI((s) => s.rightPanelOpen);
  const presenter = useUI((s) => s.presenter);
  const gaiaOpen = useUI((s) => s.gaiaOpen);
  const toggleRightPanel = useUI((s) => s.toggleRightPanel);
  const cycleBottomPanel = useUI((s) => s.cycleBottomPanel);
  const togglePresenter = useUI((s) => s.togglePresenter);
  const clearSelection = useUI((s) => s.clearSelection);
  const select = useUI((s) => s.select);
  const vw = useViewportWidth();
  const sheet = detailPresentation(vw) === 'sheet';
  const desk = stripPresentation(vw) === 'rail';
  const toasts = toastPlacement(vw);
  const [stageColumn, setStageColumn] = useState<HTMLDivElement | null>(null);
  const stageSize = useElementSize(stageColumn);
  const [snap, setSnap] = useState<SheetSnap>('peek');
  const [dragPx, setDragPx] = useState<number | null>(null);

  useEffect(() => startClockTicker(), []);
  useDeepLink(mode);
  usePresenterKeys();

  useLayoutEffect(() => {
    if (['loader', 'operations', 'analysis'].includes(mode))
      useCatalogueView.getState().enterLoader(new URLSearchParams(search).get('network') ?? undefined);
  }, [mode, search]);

  // Deep link /twin/<assetId> selects the asset.
  useEffect(() => {
    if (assetId) {
      select({ assetId });
      selectTwinCatalogue(assetId);
    }
  }, [assetId, mode, select]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // A popover, menu or overlay that handled the key (Escape closes it first) has the last word.
      if (e.defaultPrevented) return;
      if (shortcutBlocked(e.target as HTMLElement | null)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === ']') toggleRightPanel();
      else if (e.key === '[') cycleBottomPanel();
      else if (e.key === 'p' || e.key === 'P') togglePresenter();
      else if (e.key === 'Escape') clearSelection();
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleRightPanel, cycleBottomPanel, togglePresenter, clearSelection]);

  const sheetShown = sheet && rightPanelOpen && !presenter;
  const sheetFull = sheetShown && snap === 'full' && dragPx === null;
  // The stage lifts its bottom HUD above the sheet; covered by a full sheet, it is inert instead.
  const lift = sheetShown
    ? Math.min(dragPx ?? sheetHeightPx(snap, stageSize.height), sheetHeightPx('half', stageSize.height))
    : 0;

  return (
    <div className="grid h-full grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] bg-canvas">
      <LoaderRuntime />
      <AwarenessRuntime />
      <LiveTleRuntime />
      <TwinRuntime />
      <TwinLiveRuntime />
      {toasts === 'top-right' && mode !== 'overview' && <Toaster />}
      <DiveOverlay />
      <PresenterOverlay />
      <CatalogModal />
      <TwinDialogs />
      {!presenter && <TopBar mode={mode} user={user} />}
      <div className="relative flex min-h-0">
        {!presenter && <CommandStrip mode={mode} desk={desk} />}
        {gaiaOpen && !presenter && (
          <aside
            className={cn(
              'overflow-hidden border-r border-hairline bg-raised',
              desk
                ? 'w-[360px] shrink-0'
                : 'absolute inset-y-0 left-16 z-20 w-[min(360px,calc(100%-4rem))] shadow-elev-2',
            )}
            aria-label="GAIA assistant"
          >
            <Suspense fallback={<div className="p-4 text-xs text-ink-3">Loading GAIA</div>}>
              <GaiaColumn />
            </Suspense>
          </aside>
        )}
        <div
          ref={setStageColumn}
          className="relative isolate min-w-0 flex-1"
          style={{ '--sheet-h': `${lift}px` } as CSSProperties}
        >
          <main className="relative h-full overflow-hidden" inert={sheetFull}>
            <div
              id={MODE_PANEL_ID}
              role={presenter ? undefined : 'tabpanel'}
              aria-labelledby={presenter ? undefined : modeTabId(mode)}
              className="h-full w-full"
            >
              <CenterStage mode={mode} />
            </div>
          </main>
          {sheetShown && stageSize.height > 0 && (
            <DetailSheet
              mode={mode}
              snap={snap}
              containerHeight={stageSize.height}
              onSnap={setSnap}
              onClose={toggleRightPanel}
              onDragHeight={setDragPx}
            />
          )}
          {/* Tablets: toasts in the stage's bottom-right corner, clear of the HUD's controls at the top. */}
          {mode === 'overview' ? (
            <Toaster placement="overview" />
          ) : (
            toasts === 'stage' && <Toaster placement="stage" />
          )}
        </div>
        {!sheet && (
          <aside
            id="detail-panel"
            aria-label="Detail panel"
            data-testid="detail-panel"
            data-presentation="side"
            inert={!rightPanelOpen || presenter}
            className={cn(
              'shrink-0 overflow-hidden border-l border-hairline bg-raised transition-[width] duration-200',
              rightPanelOpen && !presenter ? 'w-[360px]' : 'w-0 border-l-0',
            )}
          >
            <DetailPanel mode={mode} presentation="side" />
          </aside>
        )}
      </div>
      {!presenter && (mode === 'loader' || mode === 'operations' || mode === 'analysis') && (
        <BottomPanel mode={mode} />
      )}
    </div>
  );
}
